export interface ChangeEntry {
  text: string;
}

export interface ChangeSection {
  type: "Added" | "Changed" | "Fixed" | "Removed" | "Deprecated" | "Security" | "Performance";
  entries: ChangeEntry[];
}

export interface Version {
  version: string;
  date: string | null;
  sections: ChangeSection[];
  isLatest?: boolean;
}

const SECTION_TYPES = [
  "Added",
  "Changed",
  "Fixed",
  "Removed",
  "Deprecated",
  "Security",
  "Performance",
] as const;

function parseSectionType(line: string): ChangeSection["type"] | null {
  const trimmed = line.trim();
  for (const type of SECTION_TYPES) {
    if (trimmed === `### ${type}`) {
      return type;
    }
  }
  return null;
}

function parseDate(dateStr: string): string | null {
  const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})$/);
  return match ? match[1] : null;
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function parseChangelog(content: string): Version[] {
  const lines = content.split("\n");
  const versions: Version[] = [];

  let currentVersion: Version | null = null;
  let currentSection: ChangeSection["type"] | null = null;

  for (const line of lines) {
    const versionMatch = line.match(/^## \[([^\]]+)\](?: - (.+))?$/);

    if (versionMatch) {
      if (currentVersion) {
        versions.push(currentVersion);
      }

      const heading = versionMatch[1];
      const suffix = versionMatch[2]?.trim();

      if (DATE_PATTERN.test(heading)) {
        // Format: ## [2026-02-28] — heading is a date
        currentVersion = {
          version: heading,
          date: heading,
          sections: [],
        };
      } else {
        // Standard format: ## [v1.0.0] - 2026-02-28
        currentVersion = {
          version: heading,
          date: suffix ? parseDate(suffix) : null,
          sections: [],
        };
      }

      currentSection = null;
      continue;
    }

    if (!currentVersion) continue;

    // Look for **Releases: ...** line to extract version display string
    const releasesMatch = line.match(/^\*\*Releases:\s*(.+?)\*\*$/);
    if (releasesMatch) {
      currentVersion.version = releasesMatch[1].trim();
      continue;
    }

    const sectionType = parseSectionType(line);
    if (sectionType) {
      currentSection = sectionType;
      currentVersion.sections.push({
        type: sectionType,
        entries: [],
      });
      continue;
    }

    const entryMatch = line.match(/^- (.+)$/);
    if (entryMatch && currentSection && currentVersion.sections.length > 0) {
      currentVersion.sections[currentVersion.sections.length - 1].entries.push({
        text: entryMatch[1].trim(),
      });
    }
  }

  if (currentVersion) {
    versions.push(currentVersion);
  }

  if (versions.length > 0) {
    versions[0].isLatest = true;
  }

  return versions;
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return "Unreleased";

  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
